import React, { useEffect, useState } from 'react';
import { Button, ListGroup } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import dayjs from 'dayjs';
import { enrolmentUrl, PORTAL_USER_KINDS, KIND_LABELS } from '../../../Services/PortalUsersService';
import { formatDateTime } from '../../../Utils/timezone';
import { whatsappUrl } from '../../../Utils/contactLinks';
import { firstName } from '../../Portal/portalFormat';

// The moment access changes hands: staff hold the screen out, the driver scans.
//
// The token arrives here as a prop and is never copied into state — it lives as
// long as the modal does and no longer. Nothing here logs it.

export const IPHONE_LINE = 'iPhone-on: nyisd meg a linket Safariban, és kövesd a képernyőn megjelenő lépéseket.';

// The message goes to the person who signs in: drivers read Hungarian (as the
// driver portal does); an operator login gets the English its own screens are
// written in.
const ENGLISH_LINK = (url) => 'Your Pickup Hungary portal link. '
  + `It works once and expires in 24 hours: ${url}\n\n`
  + 'On iPhone: open the link in Safari and follow the steps on screen.';

const MESSAGES = {
  [PORTAL_USER_KINDS.DRIVER]: (url) => 'Szia! Itt a Pickup Hungary sofőr portál belépési linked. '
    + `Egyszer használható, 24 óráig érvényes: ${url}\n\n${IPHONE_LINE}`,
  [PORTAL_USER_KINDS.OPERATOR]: ENGLISH_LINK,
};

const expiryLabel = (expiresAt) => {
  if (!expiresAt) return 'expires in 24h';

  const minutes = dayjs(expiresAt).diff(dayjs(), 'minute');
  if (minutes <= 0) return 'expired — generate a new link';

  const hours = Math.floor(minutes / 60);
  return hours ? `expires in ${hours}h ${minutes % 60}m` : `expires in ${minutes}m`;
};

// What this login will and will not show, so staff can say it out loud.
const scopeFor = (kind, operatorName) => {
  if (kind === PORTAL_USER_KINDS.OPERATOR) {
    return { sees: `Every trip for ${operatorName || 'this operator'}`, prices: 'Operator rates only' };
  }

  return { sees: 'Only trips assigned to them', prices: 'Never shown' };
};

// `stacked` puts the QR above the instructions in one column, for a narrow
// container (the operator portal's bottom sheet, 480px at most) where two
// columns would squeeze the code. The admin modal leaves it off.
function PortalAccessHandover({
  user, token, expiresAt, operatorName, stacked = false,
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const url = enrolmentUrl(token);
  // 'Zoltán opens …', or 'They open …' for a login with no name on it (an
  // operator).
  const name = firstName(user?.name);
  const whoOpens = name ? `${name} opens` : 'They open';
  const scope = scopeFor(user?.kind, operatorName);
  // iPhone users must not tap through in another app's browser: the link has
  // to reach Safari, whose screen then walks them through the Home Screen.
  const message = (MESSAGES[user?.kind] || MESSAGES[PORTAL_USER_KINDS.DRIVER])(url);
  // No number on file (an operator login, or a re-issue from the list): wa.me
  // without one still opens WhatsApp's contact picker with the message ready.
  const whatsappHref = whatsappUrl(user?.phone, message)
    || `https://wa.me/?text=${encodeURIComponent(message)}`;

  const column = stacked ? 'col-12' : 'col-md-6';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard unavailable (non-HTTPS origin) — the QR and WhatsApp still work.
    }
  };

  return (
    <div className="row g-4">
      <div className={`${column} d-flex flex-column align-items-center gap-3`}>
        <div className="bg-white border rounded p-3">
          <QRCodeSVG value={url} size={230} title="Enrolment QR code" />
        </div>
        <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 mb-0">
          <i className="fa fa-clock" />
          <span className="small">
            Single use ·
            {' '}
            {expiryLabel(expiresAt)}
          </span>
        </div>
      </div>

      <div className={`${column} d-flex flex-column gap-3`}>
        <div>
          <h6 className="mb-2">Hand the phone over</h6>
          <ol className="list-unstyled mb-0 d-flex flex-column gap-2">
            <li className="d-flex gap-2 small text-muted">
              <span className="fw-bold text-primary">1.</span>
              <span>
                {`${whoOpens} the camera app and point${name ? 's' : ''} it at this code.`}
              </span>
            </li>
            <li className="d-flex gap-2 small text-muted">
              <span className="fw-bold text-primary">2.</span>
              <span>
                Android: tapping the banner opens the portal, already signed in.
                iPhone: the screen first asks to add it to the home screen; the
                new icon then signs itself in.
              </span>
            </li>
            <li className="d-flex gap-2 small text-muted">
              <span className="fw-bold text-primary">3.</span>
              <span>
                On Android, add it to the home screen afterwards. Either way that
                phone stays signed in — 90 days without opening it ends the session.
              </span>
            </li>
          </ol>
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            className="flex-grow-1"
            href={whatsappHref}
            target="_blank"
            rel="noreferrer"
          >
            <i className="fab fa-whatsapp me-2" />
            Send via WhatsApp
          </Button>
          <Button variant="outline-secondary" className="flex-grow-1" onClick={handleCopy}>
            <i className={`fa ${copied ? 'fa-check' : 'fa-copy'} me-2`} />
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </div>

        <ListGroup variant="flush" className="border rounded">
          <ListGroup.Item className="d-flex justify-content-between px-3 py-2">
            <span className="small text-muted">Role</span>
            <span className="small fw-semibold">{KIND_LABELS[user?.kind] || user?.kind}</span>
          </ListGroup.Item>
          <ListGroup.Item className="d-flex justify-content-between px-3 py-2">
            <span className="small text-muted">Sees</span>
            <span className="small fw-semibold text-end">{scope.sees}</span>
          </ListGroup.Item>
          <ListGroup.Item className="d-flex justify-content-between px-3 py-2">
            <span className="small text-muted">Prices</span>
            <span className="small fw-semibold text-success">{scope.prices}</span>
          </ListGroup.Item>
          {expiresAt ? (
            <ListGroup.Item className="d-flex justify-content-between px-3 py-2">
              <span className="small text-muted">Link valid until</span>
              <span className="small fw-semibold">{formatDateTime(expiresAt)}</span>
            </ListGroup.Item>
          ) : null}
        </ListGroup>
      </div>
    </div>
  );
}

export default PortalAccessHandover;
